import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Camera, ClipboardEdit, Download, Loader2, Lock, ShieldAlert } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmergencyNotice } from "@/components/incidents/EmergencyNotice";
import api, { API_URL, formatApiError } from "@/lib/api";
import { EVIDENCE_TYPES, SG_STATUS_COLORS, SG_STATUS_LABELS, SAFEGUARDING_CATEGORY_LABELS } from "@/config/incidentCatalog";

export default function SafeguardingDetailPage() {
  const { concernId } = useParams();
  const [sg, setSg] = useState(null);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [reviewForm, setReviewForm] = useState({ assessment: "", immediate_actions: "", internal_actions: "", external_referral_required: false, agency_contacted: "", contacted_at: "", person_contacted: "", reference_number: "", parent_notification_decision: "", la_notification: false, school_notification: false, police_involvement: false, follow_up_actions: "", outcome: "", new_status: "", closure_rationale: "" });
  const [referralForm, setReferralForm] = useState({ organisation: "", contact_person: "", date: "", time: "", method: "", reference_number: "", documents_shared: "", purpose: "", follow_up_date: "", outcome: "" });
  const [statusForm, setStatusForm] = useState({ status: "", closure_rationale: "" });
  const [amendForm, setAmendForm] = useState({ reason: "", note: "" });
  const [evDesc, setEvDesc] = useState("");
  const [evType, setEvType] = useState("photograph");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/safeguarding/${concernId}`);
      setSg(data);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    }
  }, [concernId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, success) => {
    setSaving(true);
    try {
      await fn();
      toast.success(success);
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "safeguarding_evidence");
      fd.append("child_id", sg.child_id);
      fd.append("description", evDesc || "Safeguarding evidence");
      const { data: doc } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/safeguarding/${concernId}/evidence`, { document_id: doc.id, evidence_type: evType, description: evDesc });
      toast.success("Evidence attached (restricted storage)");
      setEvDesc("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (denied) {
    return (
      <div data-testid="sg-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600 mb-4" />
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Restricted record</h1>
        <p className="text-sm text-slate-600 max-w-md">This safeguarding record is restricted to authorised safeguarding staff. This access attempt has been recorded.</p>
      </div>
    );
  }
  if (!sg) {
    return <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading restricted record…</div>;
  }

  const closed = sg.status === "closed";

  return (
    <div className="space-y-4 max-w-3xl" data-testid="sg-page">
      <Link to="/safeguarding" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="sg-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to safeguarding
      </Link>
      <EmergencyNotice />

      <div className="rounded-lg border border-red-200 bg-white p-5" data-testid="sg-header">
        <div className="flex flex-wrap items-center gap-2">
          <Lock className="h-5 w-5 text-red-600" />
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="sg-number">{sg.sg_number}</h1>
          <Badge className={SG_STATUS_COLORS[sg.status]} data-testid="sg-status">{SG_STATUS_LABELS[sg.status]}</Badge>
          {sg.immediate_danger && <Badge className="bg-red-600 text-white border-transparent">IMMEDIATE DANGER</Badge>}
          {sg.staff_conduct && <Badge className="bg-purple-50 text-purple-700 border-transparent">Staff conduct — restricted handling</Badge>}
        </div>
        <p className="mt-1 text-sm text-slate-600">
          Case {sg.child?.case_id} · Category: {SAFEGUARDING_CATEGORY_LABELS[sg.category] || sg.category} · Reported by {sg.reporter_name} · {(sg.submitted_at || "").slice(0, 10)}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {!closed && (
            <>
              <Button className="h-11 bg-primary text-white" data-testid="sg-review-button" onClick={() => setDialog("review")}>Record review</Button>
              <Button variant="outline" className="h-11" data-testid="sg-status-button" onClick={() => setDialog("status")}>Change status</Button>
              <Button variant="outline" className="h-11" data-testid="sg-referral-button" onClick={() => setDialog("referral")}>Record external referral</Button>
            </>
          )}
          <Button variant="outline" className="h-11" data-testid="sg-amend-button" onClick={() => setDialog("amend")} disabled={closed}>
            <ClipboardEdit className="h-4 w-4 mr-1" /> Add amendment
          </Button>
          <a href={`${API_URL}/safeguarding/${sg.id}/export`} target="_blank" rel="noreferrer" data-testid="sg-export-button"
            className="inline-flex h-11 items-center gap-2 rounded-md border border-red-300 px-4 text-sm font-semibold text-red-700 hover:bg-red-50">
            <Download className="h-4 w-4" /> Export (audit-logged)
          </a>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Concern record (concern/allegation/report — not a finding)</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><dt className="text-xs text-slate-500">Identified</dt><dd>{sg.identified_date} {sg.identified_time || ""}</dd></div>
            <div><dt className="text-xs text-slate-500">Location</dt><dd>{sg.location || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Child currently safe</dt><dd>{(sg.child_safe || "—").toUpperCase()}</dd></div>
            <div><dt className="text-xs text-slate-500">Manager notified</dt><dd>{sg.manager_notified ? "Yes" : "No"}</dd></div>
            <div><dt className="text-xs text-slate-500">Emergency services</dt><dd>{sg.emergency_services ? "Yes" : "No"}</dd></div>
          </dl>
          <p><b>Factual description:</b> {sg.factual_description}</p>
          {sg.child_said && <p><b>What the child said:</b> {sg.child_said}</p>}
          {sg.observed && <p><b>Observed:</b> {sg.observed}</p>}
          {sg.immediate_action && <p><b>Immediate action:</b> {sg.immediate_action}</p>}
          {sg.other_info && <p><b>Other information:</b> {sg.other_info}</p>}
        </CardContent>
      </Card>

      {sg.disclosure && (
        <Card data-testid="sg-disclosure-view">
          <CardHeader><CardTitle className="text-base">Child's disclosure / account</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            {sg.disclosure.context && <p><b>Context:</b> {sg.disclosure.context}</p>}
            {sg.disclosure.account && <p><b>Child's account:</b> {sg.disclosure.account}</p>}
            {sg.disclosure.questions_asked && <p><b>Questions asked:</b> {sg.disclosure.questions_asked}</p>}
            {sg.disclosure.immediate_response && <p><b>Immediate response:</b> {sg.disclosure.immediate_response}</p>}
            {sg.disclosure.who_informed && <p><b>Who was informed:</b> {sg.disclosure.who_informed}</p>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Management reviews ({sg.reviews?.length ?? 0})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {!sg.reviews?.length && <p className="text-sm text-slate-600">No reviews recorded yet.</p>}
          {sg.reviews?.map((r, i) => (
            <div key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`sg-review-${i}`}>
              <p className="font-medium">{r.reviewer_name} · {(r.at || "").slice(0, 16).replace("T", " ")}</p>
              <p className="text-slate-700">{r.assessment}</p>
              {r.outcome && <p className="text-xs text-slate-600">Outcome: {r.outcome}</p>}
              {r.closure_rationale && <p className="text-xs text-slate-600">Closure rationale: {r.closure_rationale}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">External referrals ({sg.external_referrals?.length ?? 0})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {!sg.external_referrals?.length && <p className="text-sm text-slate-600">No external referrals recorded.</p>}
          {sg.external_referrals?.map((r, i) => (
            <div key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`sg-referral-${i}`}>
              <p className="font-medium">{r.organisation} · ref {r.reference_number || "—"}</p>
              <p className="text-xs text-slate-600">{r.date} {r.time || ""} · {r.method || "—"} · shared by {r.shared_by_name}{r.outcome ? ` · outcome: ${r.outcome}` : ""}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Evidence (restricted)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(sg.evidence || []).map((e, i) => (
            <p key={i} className="text-xs text-slate-600" data-testid={`sg-evidence-${i}`}>
              {EVIDENCE_TYPES.find((t) => t.value === e.evidence_type)?.label || e.evidence_type}: {e.description || "—"} — {e.uploaded_by_name}
            </p>
          ))}
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={evType} onValueChange={setEvType}>
              <SelectTrigger data-testid="sg-evidence-type" className="h-11 w-44"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{EVIDENCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="h-11 flex-1 min-w-[160px]" placeholder="Description" data-testid="sg-evidence-desc" value={evDesc} onChange={(e) => setEvDesc(e.target.value)} />
            <Button variant="outline" className="h-11" data-testid="sg-evidence-upload" onClick={() => fileRef.current?.click()}>
              <Camera className="h-4 w-4 mr-1" /> Attach evidence
            </Button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { uploadEvidence(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        </CardContent>
      </Card>

      {sg.amendments?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Amendment history</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {sg.amendments.map((a, i) => (
              <div key={i} className="rounded-md border border-border p-3 text-sm" data-testid={`sg-amendment-${i}`}>
                <p className="font-medium">{a.reason}</p>
                {a.note && <p className="text-slate-600">{a.note}</p>}
                <p className="text-xs text-slate-500">{(a.at || "").slice(0, 16).replace("T", " ")} by {a.author_name}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialog === "review"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Safeguarding management review</DialogTitle><DialogDescription>Decisions remain with authorised professionals — the software records, it does not decide.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Assessment of concern *</Label><Textarea data-testid="sg-review-assessment" rows={3} value={reviewForm.assessment} onChange={(e) => setReviewForm((f) => ({ ...f, assessment: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Immediate actions</Label><Textarea data-testid="sg-review-immediate" rows={2} value={reviewForm.immediate_actions} onChange={(e) => setReviewForm((f) => ({ ...f, immediate_actions: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Follow-up actions</Label><Textarea data-testid="sg-review-followup" rows={2} value={reviewForm.follow_up_actions} onChange={(e) => setReviewForm((f) => ({ ...f, follow_up_actions: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Parent/carer notification decision</Label><Input data-testid="sg-review-parent" value={reviewForm.parent_notification_decision} onChange={(e) => setReviewForm((f) => ({ ...f, parent_notification_decision: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Outcome</Label><Textarea data-testid="sg-review-outcome" rows={2} value={reviewForm.outcome} onChange={(e) => setReviewForm((f) => ({ ...f, outcome: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>New status</Label>
              <Select value={reviewForm.new_status} onValueChange={(v) => setReviewForm((f) => ({ ...f, new_status: v }))}>
                <SelectTrigger data-testid="sg-review-status" className="h-11"><SelectValue placeholder="Under review (default)" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(SG_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {reviewForm.new_status === "closed" && (
              <div className="space-y-1.5"><Label>Closure rationale *</Label><Textarea data-testid="sg-review-closure" rows={2} value={reviewForm.closure_rationale} onChange={(e) => setReviewForm((f) => ({ ...f, closure_rationale: e.target.value }))} /></div>
            )}
            <Button data-testid="sg-review-submit" className="h-11 w-full bg-primary text-white"
              disabled={saving || !reviewForm.assessment.trim() || (reviewForm.new_status === "closed" && !reviewForm.closure_rationale.trim())}
              onClick={() => act(() => api.post(`/safeguarding/${sg.id}/review`, reviewForm), "Review recorded")}>Record review</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "status"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Change status</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={statusForm.status} onValueChange={(v) => setStatusForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="sg-status-select" className="h-11"><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent className="bg-white">
                {Object.entries(SG_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {statusForm.status === "closed" && (
              <Textarea data-testid="sg-status-closure" rows={2} placeholder="Closure rationale (required)" value={statusForm.closure_rationale} onChange={(e) => setStatusForm((f) => ({ ...f, closure_rationale: e.target.value }))} />
            )}
            <Button data-testid="sg-status-submit" className="h-11 w-full bg-primary text-white"
              disabled={saving || !statusForm.status || (statusForm.status === "closed" && !statusForm.closure_rationale.trim())}
              onClick={() => act(() => api.post(`/safeguarding/${sg.id}/status`, statusForm), "Status updated")}>Update status</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "referral"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Record external referral</DialogTitle><DialogDescription>Recording only — the system never sends safeguarding information externally.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Organisation / agency *</Label><Input data-testid="referral-org" value={referralForm.organisation} onChange={(e) => setReferralForm((f) => ({ ...f, organisation: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact person</Label><Input data-testid="referral-person" value={referralForm.contact_person} onChange={(e) => setReferralForm((f) => ({ ...f, contact_person: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Reference number</Label><Input data-testid="referral-ref" value={referralForm.reference_number} onChange={(e) => setReferralForm((f) => ({ ...f, reference_number: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Date</Label><Input data-testid="referral-date" type="date" value={referralForm.date} onChange={(e) => setReferralForm((f) => ({ ...f, date: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Method</Label><Input data-testid="referral-method" value={referralForm.method} onChange={(e) => setReferralForm((f) => ({ ...f, method: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Documents shared</Label><Input data-testid="referral-docs" value={referralForm.documents_shared} onChange={(e) => setReferralForm((f) => ({ ...f, documents_shared: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Outcome / response</Label><Textarea data-testid="referral-outcome" rows={2} value={referralForm.outcome} onChange={(e) => setReferralForm((f) => ({ ...f, outcome: e.target.value }))} /></div>
            <Button data-testid="referral-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !referralForm.organisation.trim()}
              onClick={() => act(() => api.post(`/safeguarding/${sg.id}/referrals`, referralForm), "Referral recorded")}>Record referral</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "amend"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add amendment</DialogTitle><DialogDescription>The original submission is never overwritten.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Reason *</Label><Input data-testid="sg-amend-reason" value={amendForm.reason} onChange={(e) => setAmendForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Amendment detail</Label><Textarea data-testid="sg-amend-note" rows={3} value={amendForm.note} onChange={(e) => setAmendForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <Button data-testid="sg-amend-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !amendForm.reason.trim()}
              onClick={() => act(() => api.post(`/safeguarding/${sg.id}/amend`, amendForm), "Amendment recorded")}>Record amendment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
